import { useState } from "react";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChevronRight,
  Check,
  Headphones,
  DollarSign,
  Repeat,
  Send,
  Cpu,
  Megaphone,
  ShieldCheck,
  Download,
  FileText,
  Layers,
  Image as ImageIcon,
  Share2,
  Clock,
  Smartphone,
  CreditCard,
  Server,
} from "lucide-react";
import CTASection from "@/components/CTASection";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import { PartnershipIllustration } from "@/components/illustrations/FintechIllustrations";


/* ───────────────────────── Data ───────────────────────── */
const TERMINAL_RESOURCES = [
  {
    brand: "Valor PayTech",
    short: "Valor",
    title: "Counter Top, Wireless & Android POS",
    description:
      "The ValorPay Tech Resource Center is your one-stop hub for guides, technical documentation, and support to optimize your payment processing.",
    href: "https://valorpaytech.com/",
    icon: CreditCard,
    primaryImg: "/terminals/valor-vp550.png",
    secondaryImg: "/terminals/valor-vl100-pro.png",
    accentTag: "Counter Top · Wireless · Android",
  },
  {
    brand: "Dejavoo",
    short: "Dejavoo",
    title: "Android & Linux Devices",
    description:
      "From your sales counter terminal to the payment gateway platform, and from the back office to the cloud, Dejavoo provides comprehensive solutions that elevate your business.",
    href: "https://dejavoo.io/resource-center/",
    icon: Server,
    primaryImg: "/terminals/dejavoo-qd4.png",
    secondaryImg: "/terminals/dejavoo-z11.png",
    accentTag: "Android · Linux · Cloud-Connected",
  },
  {
    brand: "PAX",
    short: "PAX",
    title: "Counter Top, mPOS & Pin Pads",
    description:
      "Whatever your industry, PAX offers a range of POS products and solutions tailored to meet your specific needs and propel your business forward.",
    href: "https://www.pax.us/support/documents/",
    icon: Smartphone,
    primaryImg: "/terminals/pax-a80.png",
    secondaryImg: "/terminals/pax-a35.png",
    accentTag: "Counter Top · mPOS · Pin Pads",
  },
];

const MARKETING_PLACEHOLDERS = [
  {
    icon: FileText,
    title: "Partner Flyers",
    description: "Co-branded one-pagers designed to introduce Swipetronics to your prospects.",
  },
  {
    icon: Layers,
    title: "Sales Sheets",
    description: "Pricing, processing, and POS program sheets formatted for in-person and email.",
  },
  {
    icon: ImageIcon,
    title: "Social Media Graphics",
    description: "Ready-to-post graphics and short captions for LinkedIn, Instagram, and X.",
  },
];

const COMPLIANCE_DOCS = [
  {
    title: "Dual Pricing",
    description: "Legal guide covering dual pricing program structure and merchant requirements.",
    file: "swipetronics-dual-pricing-legal-guide-clean-logo.pdf",
    preview: "swipetronics-dual-pricing-legal-guide-clean-logo-1.png",
  },
  {
    title: "U.S. Merchant Surcharge Q&A",
    description: "Frequently asked questions about credit card surcharging in the United States.",
    file: "swipetronics-surcharge-q-and-a-clean-logo.pdf",
    preview: "swipetronics-surcharge-q-and-a-clean-logo-1.png",
  },
  {
    title: "Merchant Surcharging Considerations & Requirements",
    description: "Considerations, notice rules, and merchant requirements for surcharging.",
    file: "swipetronics-merchant-surcharge-requirements-clean-logo.pdf",
    preview: "swipetronics-merchant-surcharge-requirements-clean-logo-1.png",
  },
  {
    title: "Credit Card Surcharge Guidance",
    description: "Official one-page reference guide for credit card surcharging.",
    file: "2024-01-17-DOS-DCP-Credit-Card-Surcharge-One-Page-Reference-Guide.pdf",
    preview: "2024-01-17-DOS-DCP-Credit-Card-Surcharge-One-Page-Reference-Guide-1.png",
  },
  {
    title: "Credit Card Surcharge Violations",
    description: "Consumer-facing poster covering surcharge violations and reporting.",
    file: "2024-04-22-credit-card-surcharge-violations-poster-20in-x-30in.pdf",
    preview: "2024-04-22-credit-card-surcharge-violations-poster-20in-x-30in-1.png",
  },
];

const TABS = [
  { id: "terminals", label: "Terminal Resource Center", icon: Cpu },
  { id: "marketing", label: "Marketing Material", icon: Megaphone },
  { id: "compliance", label: "Compliance Section", icon: ShieldCheck },
];

/* ───────────────────────── Page ───────────────────────── */
export default function Partnerships() {
  const [activeTab, setActiveTab] = useState("terminals");

  return (
    <div data-testid="partnerships-page">
      <Seo {...getSeoForPath("/partnerships")} />
      {/* HERO — unchanged */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)" }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)" }} />
        <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
        <div aria-hidden="true" className="absolute top-[30%] right-[2%] w-2 h-2 rounded-full bg-[var(--swipe-accent)] opacity-70 pointer-events-none" />
        <div aria-hidden="true" className="absolute top-[18%] left-[40%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-60 pointer-events-none" />
        <div aria-hidden="true" className="absolute bottom-[20%] left-[8%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-50 pointer-events-none" />

        <ParticleField density={36} connectDistance={130} className="absolute inset-0 w-full h-full" />

        <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
          <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">Partnerships</span>
          </nav>

          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-12 items-center">
            <Reveal>
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                  Partner Program
                </div>
                <h1 className="font-display-tight text-[2.75rem] sm:text-[3.5rem] lg:text-[4.25rem] leading-[1.04] font-extrabold text-white tracking-[-0.025em]">
                  <span className="text-[var(--swipe-accent)]">Partnerships</span>
                </h1>
                <p className="mt-6 text-[17px] text-white/75 leading-[1.7] max-w-xl">
                  Explore partnership opportunities with Swipetronics and discover solutions
                  designed to support growth, collaboration, and long-term business success.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/contact" className="btn-primary-swipe text-[15px] py-3.5 px-7" data-testid="partnerships-hero-cta-contact">
                    Become a Partner <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/about" className="btn-outline-light text-[15px] py-3.5 px-7" data-testid="partnerships-hero-cta-about">
                    Learn About Us
                  </Link>
                </div>
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <PartnershipIllustration transparent className="relative w-full h-auto max-w-[560px] mx-auto" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ───────────── REFERRAL PARTNERS (expanded) ───────────── */}
      <section className="relative bg-white overflow-hidden border-b border-line" data-testid="partnerships-referral-section">
        <div aria-hidden="true" className="absolute -top-32 -right-20 w-[460px] h-[460px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.08)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.07)", filter: "blur(110px)" }} />

        <div className="container-swipe relative py-20 lg:py-24">
          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-12 lg:gap-16 items-center">
            <Reveal>
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--swipe-accent-soft)] border border-[var(--swipe-accent)]/20 text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--swipe-accent)] mb-5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)]" />
                  Referral Partners
                </div>
                <h2 className="font-display-tight text-[2rem] sm:text-[2.75rem] lg:text-[3rem] font-extrabold leading-[1.08] text-[var(--swipe-navy)] tracking-[-0.025em]">
                  Refer a business. <span className="text-[var(--swipe-accent)]">Earn for it.</span>
                </h2>
                <p className="mt-6 text-[16.5px] text-slate-600 leading-[1.8] max-w-xl">
                  Know a business that needs better payment processing, POS systems, or lower
                  monthly processing costs? Send the lead to Swipetronics. If the merchant signs
                  up and starts processing, you can get paid for the referral.
                </p>
                <p className="mt-5 text-[16.5px] text-slate-600 leading-[1.8] max-w-xl">
                  Qualified referral partners may also be eligible for{" "}
                  <span className="font-semibold text-[var(--swipe-navy)]">monthly residual payments</span>
                  , allowing you to earn ongoing income from businesses you refer.
                </p>

                <ul className="mt-8 grid gap-3 max-w-xl">
                  {[
                    "Send a lead — no quota, no exclusivity, no minimums",
                    "Earn a payout when the merchant signs and starts processing",
                    "Qualified partners may receive monthly residuals on active accounts",
                    "Transparent reporting on every account you refer",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[15px] text-slate-700 leading-[1.65]">
                      <span className="mt-1 w-5 h-5 rounded-full bg-[var(--swipe-accent)]/12 text-[var(--swipe-accent)] flex items-center justify-center shrink-0">
                        <Check className="w-3.5 h-3.5" strokeWidth={3.2} />
                      </span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-9 flex flex-wrap gap-3">
                  <Link to="/contact" className="btn-primary-swipe text-[15px] py-3.5 px-7" data-testid="partnerships-referral-cta-send">
                    Send a Referral <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/contact" className="btn-outline-swipe text-[15px] py-3.5 px-7" data-testid="partnerships-referral-cta-talk">
                    Talk to Our Team
                  </Link>
                </div>
                <p className="mt-5 text-[12.5px] text-slate-500 leading-relaxed max-w-xl">
                  Referral payouts and monthly residual eligibility depend on the account,
                  program, and partnership agreement. Final terms are confirmed during onboarding.
                </p>
              </div>
            </Reveal>

            {/* Right column — 3 value cards */}
            <Reveal variant="slide-right" delay={120}>
              <div className="grid gap-5">
                {[
                  {
                    icon: Send,
                    title: "Send the lead",
                    text: "Introduce a business owner to Swipetronics by phone, email, or referral form. We handle the rest.",
                  },
                  {
                    icon: DollarSign,
                    title: "Get paid on activation",
                    text: "When the merchant signs and starts processing, you can earn a payout for the successful referral.",
                  },
                  {
                    icon: Repeat,
                    title: "Earn monthly residuals",
                    text: "Qualified partners may earn ongoing monthly residual income from active accounts they referred.",
                  },
                ].map((card, i) => {
                  const Icon = card.icon;
                  const isAccent = i === 1;
                  return (
                    <div
                      key={card.title}
                      className={`group relative flex items-start gap-5 rounded-2xl p-6 lg:p-7 border transition-all duration-300 hover:-translate-y-1 hover:border-[var(--swipe-accent)] cursor-default ${
                        isAccent
                          ? "bg-[var(--swipe-navy)] border-transparent shadow-[0_18px_40px_-22px_rgba(6,26,58,0.55)] hover:shadow-[0_28px_56px_-22px_rgba(0,114,255,0.55)]"
                          : "bg-[var(--swipe-accent-soft)] border-[var(--swipe-accent)]/15 shadow-[0_10px_30px_-22px_rgba(6,26,58,0.20)] hover:shadow-[0_24px_50px_-22px_rgba(0,114,255,0.35)]"
                      }`}
                    >
                      <div
                        className={`w-14 h-14 shrink-0 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3 ${
                          isAccent
                            ? "bg-[var(--swipe-accent)] shadow-[0_14px_28px_-10px_rgba(0,114,255,0.55)]"
                            : "bg-white shadow-[0_10px_22px_-12px_rgba(0,114,255,0.35)]"
                        }`}
                      >
                        <Icon className={`w-7 h-7 ${isAccent ? "text-white" : "text-[var(--swipe-accent)]"}`} strokeWidth={2.2} />
                      </div>
                      <div>
                        <h3
                          className="font-display-tight text-[19px] font-extrabold leading-tight tracking-[-0.01em]"
                          style={{ color: isAccent ? "#FFFFFF" : "var(--swipe-navy)" }}
                        >
                          {card.title}
                        </h3>
                        <p
                          className="mt-2 text-[14.5px] leading-[1.7]"
                          style={{ color: isAccent ? "rgba(255,255,255,0.78)" : "rgb(71, 85, 105)" }}
                        >
                          {card.text}
                        </p>
                      </div>
                      <span aria-hidden="true" className="absolute top-0 left-6 right-6 h-[2px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 bg-[var(--swipe-accent)] rounded-full" />
                    </div>
                  );
                })}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ───────────── PARTNER RESOURCE CENTER (tabbed) ───────────── */}
      <section className="relative bg-gradient-to-b from-[var(--swipe-muted)] to-white overflow-hidden border-b border-line" data-testid="partner-resource-center">
        <div aria-hidden="true" className="absolute -top-32 left-1/2 -translate-x-1/2 w-[640px] h-[640px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(0,114,255,0.07) 0%, transparent 65%)" }} />

        <div className="container-swipe relative py-20 lg:py-24">
          <Reveal>
            <div className="max-w-[920px] mx-auto text-center mb-12">
              <div className="inline-flex items-center gap-2 mb-5">
                <span className="h-px w-10 bg-[var(--swipe-accent)]/40" />
                <span className="text-[11px] uppercase tracking-[0.24em] font-bold text-[var(--swipe-accent)]">Partner Resources</span>
                <span className="h-px w-10 bg-[var(--swipe-accent)]/40" />
              </div>
              <h2 className="font-display-tight text-[2rem] sm:text-[2.75rem] lg:text-[3rem] font-extrabold leading-[1.1] text-[var(--swipe-navy)] tracking-[-0.02em]">
                Everything our partners need, <span className="text-[var(--swipe-accent)]">in one place</span>.
              </h2>
              <p className="mt-5 text-[16px] text-slate-600 leading-[1.75] max-w-[720px] mx-auto">
                Hardware documentation, co-branded marketing materials, and current compliance
                resources &mdash; all curated for Swipetronics partners.
              </p>
            </div>
          </Reveal>

          {/* Tabs */}
          <Reveal delay={80}>
            <div
              className="bg-white rounded-2xl border border-line shadow-[0_18px_50px_-30px_rgba(6,26,58,0.25)] p-2 mx-auto max-w-[820px] grid grid-cols-1 sm:grid-cols-3 gap-2"
              role="tablist"
              data-testid="partner-resource-tabs"
            >
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`partner-tab-${tab.id}`}
                    className={`relative flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl text-[12.5px] uppercase tracking-[0.14em] font-extrabold transition-all duration-300 ${
                      isActive
                        ? "bg-[var(--swipe-navy)] text-white shadow-[0_14px_28px_-12px_rgba(6,26,58,0.45)]"
                        : "bg-transparent text-[var(--swipe-navy)] hover:bg-[var(--swipe-accent-soft)]"
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${isActive ? "text-[var(--swipe-accent)]" : "text-[var(--swipe-accent)]"}`} strokeWidth={2.4} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </Reveal>

          {/* Tab content */}
          <div className="mt-12">
            {/* TERMINALS — stacked horizontal rows */}
            {activeTab === "terminals" && (
              <div className="grid gap-7 max-w-[1180px] mx-auto" data-testid="partner-tab-content-terminals">
                {TERMINAL_RESOURCES.map((r, i) => {
                  const Icon = r.icon;
                  const imageRight = i % 2 === 0;
                  return (
                    <Reveal key={r.brand} delay={i * 100}>
                      <div className="group relative bg-white rounded-3xl border border-line overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--swipe-accent)]/40 hover:shadow-[0_36px_70px_-26px_rgba(0,114,255,0.30)] shadow-[0_20px_50px_-32px_rgba(6,26,58,0.30)]">
                        <div className={`grid lg:grid-cols-2 ${imageRight ? "" : "lg:[&>*:first-child]:order-2"}`}>
                          {/* IMAGE BLOCK */}
                          <div className={`relative min-h-[340px] lg:min-h-[420px] bg-gradient-to-br from-[var(--swipe-accent-soft)] via-white to-[var(--swipe-accent-soft)] flex items-center justify-center overflow-hidden ${imageRight ? "lg:order-2" : "lg:order-1"}`}>
                            {/* dotted backdrop */}
                            <svg className="absolute inset-0 w-full h-full opacity-50" aria-hidden="true">
                              <defs>
                                <pattern id={`tr-dots-${r.short}`} width="16" height="16" patternUnits="userSpaceOnUse">
                                  <circle cx="2" cy="2" r="1" fill="#0072FF" fillOpacity="0.18" />
                                </pattern>
                              </defs>
                              <rect width="100%" height="100%" fill={`url(#tr-dots-${r.short})`} />
                            </svg>
                            {/* glow blobs */}
                            <div aria-hidden="true" className="absolute -top-16 -right-12 w-[260px] h-[260px] rounded-full" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(60px)" }} />
                            <div aria-hidden="true" className="absolute -bottom-16 -left-12 w-[240px] h-[240px] rounded-full" style={{ background: "rgba(6,26,58,0.08)", filter: "blur(60px)" }} />

                            {/* brand chip */}
                            <div className="absolute top-5 left-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--swipe-navy)] text-white text-[10.5px] uppercase tracking-[0.18em] font-extrabold shadow-[0_8px_18px_-6px_rgba(6,26,58,0.45)]">
                              <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                              {r.short}
                            </div>

                            {/* product images — primary big + secondary smaller offset */}
                            <div className="relative w-full h-full flex items-center justify-center p-8">
                              <img
                                src={r.secondaryImg}
                                alt=""
                                aria-hidden="true"
                                className="absolute right-[18%] bottom-[14%] w-[40%] max-w-[180px] drop-shadow-[0_22px_36px_rgba(6,26,58,0.20)] opacity-90 transition-transform duration-500 group-hover:translate-y-[-6px] group-hover:rotate-[-3deg]"
                              />
                              <img
                                src={r.primaryImg}
                                alt={`${r.brand} payment device`}
                                className="relative w-[68%] max-w-[280px] drop-shadow-[0_30px_44px_rgba(6,26,58,0.30)] transition-transform duration-500 group-hover:translate-y-[-4px] group-hover:scale-[1.02]"
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                              />
                            </div>

                            {/* accent tag pill */}
                            <div className="absolute bottom-5 left-5 right-5 flex justify-start">
                              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/85 backdrop-blur-sm border border-[var(--swipe-accent)]/20 text-[10.5px] uppercase tracking-[0.16em] font-bold text-[var(--swipe-navy)] shadow-[0_8px_18px_-8px_rgba(6,26,58,0.25)]">
                                <span className="w-1 h-1 rounded-full bg-[var(--swipe-accent)]" />
                                {r.accentTag}
                              </span>
                            </div>
                          </div>

                          {/* TEXT BLOCK */}
                          <div className={`flex flex-col justify-center p-8 sm:p-10 lg:p-12 ${imageRight ? "lg:order-1" : "lg:order-2"}`}>
                            <div className="inline-flex items-center gap-2 w-fit">
                              <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] shadow-[0_8px_18px_-8px_rgba(0,114,255,0.35)]">
                                <Icon className="w-5 h-5" strokeWidth={2.4} />
                              </span>
                              <span className="text-[11.5px] uppercase tracking-[0.22em] font-extrabold text-[var(--swipe-accent)]">
                                {r.brand}
                              </span>
                            </div>
                            <h3 className="mt-5 font-display-tight text-[1.75rem] sm:text-[2.15rem] lg:text-[2.35rem] font-extrabold text-[var(--swipe-navy)] leading-[1.1] tracking-[-0.02em]">
                              {r.title}
                            </h3>
                            <p className="mt-5 text-[15.5px] text-slate-600 leading-[1.8] max-w-[520px]">
                              {r.description}
                            </p>
                            <a
                              href={r.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-8 inline-flex items-center gap-2 btn-primary-swipe text-[14.5px] py-3.5 px-7 self-start"
                              data-testid={`terminal-resource-${r.short.toLowerCase()}-cta`}
                            >
                              Know More <ArrowRight className="w-4 h-4" />
                            </a>
                          </div>
                        </div>

                        {/* top accent line on hover */}
                        <span aria-hidden="true" className="absolute top-0 left-10 right-10 h-[2px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 bg-[var(--swipe-accent)] rounded-full" />
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            )}

            {/* MARKETING */}
            {activeTab === "marketing" && (
              <div data-testid="partner-tab-content-marketing">
                <Reveal>
                  <div className="max-w-[760px] mx-auto text-center mb-10">
                    <h3 className="font-display-tight text-[1.7rem] sm:text-[2rem] font-extrabold text-[var(--swipe-navy)] leading-[1.15] tracking-[-0.02em]">
                      Marketing Material
                    </h3>
                    <p className="mt-4 text-[15.5px] text-slate-600 leading-[1.75]">
                      Sales and marketing resources for Swipetronics partners will be added here.
                    </p>
                  </div>
                </Reveal>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
                  {MARKETING_PLACEHOLDERS.map((m, i) => {
                    const Icon = m.icon;
                    return (
                      <Reveal key={m.title} delay={i * 80}>
                        <div className="group relative bg-white rounded-2xl border border-line p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--swipe-accent)]/40 hover:shadow-[0_30px_60px_-26px_rgba(0,114,255,0.35)] shadow-[0_18px_40px_-30px_rgba(6,26,58,0.30)] h-full">
                          <div className="w-14 h-14 rounded-xl bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3 shadow-[0_10px_22px_-12px_rgba(0,114,255,0.35)]">
                            <Icon className="w-[26px] h-[26px]" strokeWidth={2.2} />
                          </div>
                          <div className="mt-5 flex items-center gap-2">
                            <h4 className="font-display-tight text-[18px] font-extrabold text-[var(--swipe-navy)] leading-tight tracking-[-0.01em]">
                              {m.title}
                            </h4>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] text-[9.5px] uppercase tracking-[0.18em] font-bold">
                              <Clock className="w-3 h-3" strokeWidth={2.8} />
                              Coming Soon
                            </span>
                          </div>
                          <p className="mt-3 text-[14.5px] text-slate-600 leading-[1.7]">
                            {m.description}
                          </p>
                          <span aria-hidden="true" className="absolute top-0 left-6 right-6 h-[2px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 bg-[var(--swipe-accent)] rounded-full" />
                        </div>
                      </Reveal>
                    );
                  })}
                </div>
                <Reveal delay={300}>
                  <div className="mt-12 max-w-[700px] mx-auto rounded-2xl bg-[var(--swipe-navy)] text-white p-7 sm:p-9 text-center border border-white/10">
                    <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-[var(--swipe-accent)]/15 text-[var(--swipe-accent)] mb-4">
                      <Share2 className="w-5 h-5" strokeWidth={2.4} />
                    </div>
                    <h4 className="font-display-tight text-[1.4rem] sm:text-[1.6rem] font-extrabold leading-tight tracking-[-0.015em]" style={{ color: "#FFFFFF" }}>
                      Need something specific in the meantime?
                    </h4>
                    <p className="mt-3 text-[15px] leading-[1.7]" style={{ color: "rgba(255,255,255,0.75)" }}>
                      Reach out and we&apos;ll send co-branded materials tailored to your audience.
                    </p>
                    <Link to="/contact" className="mt-6 inline-flex items-center gap-2 btn-primary-swipe text-[14px] py-3 px-6" data-testid="marketing-contact-cta">
                      Request Materials <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </Reveal>
              </div>
            )}

            {/* COMPLIANCE */}
            {activeTab === "compliance" && (
              <div data-testid="partner-tab-content-compliance">
                <Reveal>
                  <div className="max-w-[760px] mx-auto text-center mb-10">
                    <h3 className="font-display-tight text-[1.7rem] sm:text-[2rem] font-extrabold text-[var(--swipe-navy)] leading-[1.15] tracking-[-0.02em]">
                      Compliance Resources
                    </h3>
                    <p className="mt-4 text-[15.5px] text-slate-600 leading-[1.75]">
                      Current guidance documents on dual pricing, surcharging, and merchant
                      requirements &mdash; ready to download.
                    </p>
                  </div>
                </Reveal>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7 max-w-[1180px] mx-auto">
                  {COMPLIANCE_DOCS.map((doc, i) => (
                    <Reveal key={doc.file} delay={i * 80}>
                      <a
                        href={`/downloads/${doc.file}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`compliance-card-${i}`}
                        className="group relative flex flex-col rounded-2xl bg-white border border-line overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--swipe-accent)]/40 hover:shadow-[0_30px_60px_-26px_rgba(0,114,255,0.30)] shadow-[0_16px_40px_-30px_rgba(6,26,58,0.28)] h-full"
                      >
                        {/* PDF preview thumbnail */}
                        <div className="relative aspect-[3/4] bg-gradient-to-br from-[var(--swipe-accent-soft)] via-white to-[var(--swipe-accent-soft)] overflow-hidden flex items-center justify-center p-5">
                          {/* dotted backdrop */}
                          <svg className="absolute inset-0 w-full h-full opacity-40" aria-hidden="true">
                            <defs>
                              <pattern id={`comp-dots-${i}`} width="14" height="14" patternUnits="userSpaceOnUse">
                                <circle cx="2" cy="2" r="1" fill="#0072FF" fillOpacity="0.16" />
                              </pattern>
                            </defs>
                            <rect width="100%" height="100%" fill={`url(#comp-dots-${i})`} />
                          </svg>
                          {/* page frame */}
                          <div className="relative w-full h-full max-w-[260px] mx-auto">
                            <img
                              src={`/downloads/previews/${doc.preview}`}
                              alt={`${doc.title} — first page preview`}
                              className="w-full h-full object-contain object-top rounded-md bg-white shadow-[0_22px_50px_-18px_rgba(6,26,58,0.35),0_2px_8px_rgba(6,26,58,0.10)] ring-1 ring-[var(--swipe-navy)]/8 transition-transform duration-500 group-hover:scale-[1.025] group-hover:-rotate-[0.4deg]"
                              loading="lazy"
                            />
                            {/* corner curl shadow */}
                            <span
                              aria-hidden="true"
                              className="absolute top-0 right-0 w-7 h-7"
                              style={{ background: "linear-gradient(135deg, transparent 50%, rgba(6,26,58,0.10) 50%)" }}
                            />
                          </div>
                          {/* PDF chip */}
                          <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--swipe-navy)] text-white text-[10px] uppercase tracking-[0.20em] font-extrabold shadow-[0_8px_18px_-6px_rgba(6,26,58,0.45)]">
                            <FileText className="w-3 h-3 text-[var(--swipe-accent)]" strokeWidth={3} />
                            PDF
                          </span>
                          {/* hover overlay: zoom hint */}
                          <span className="absolute inset-0 flex items-center justify-center bg-[var(--swipe-navy)]/0 group-hover:bg-[var(--swipe-navy)]/12 transition-colors duration-300 pointer-events-none">
                            <span className="opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white text-[var(--swipe-navy)] text-[11px] uppercase tracking-[0.18em] font-extrabold shadow-[0_14px_30px_-10px_rgba(6,26,58,0.45)]">
                              <Download className="w-3.5 h-3.5 text-[var(--swipe-accent)]" strokeWidth={2.8} />
                              View / Download
                            </span>
                          </span>
                        </div>

                        {/* body */}
                        <div className="flex flex-col flex-1 p-5 sm:p-6">
                          <h4 className="font-display-tight text-[16.5px] font-extrabold text-[var(--swipe-navy)] leading-[1.25] tracking-[-0.01em]">
                            {doc.title}
                          </h4>
                          <p className="mt-2 text-[13.5px] text-slate-600 leading-[1.6] flex-1">
                            {doc.description}
                          </p>
                          <span
                            className="mt-5 inline-flex items-center justify-center gap-2 self-start btn-primary-swipe text-[13px] py-2.5 px-5"
                            data-testid={`compliance-download-${i}`}
                          >
                            <Download className="w-4 h-4" strokeWidth={2.6} />
                            Download
                          </span>
                        </div>

                        {/* top accent line on hover */}
                        <span aria-hidden="true" className="absolute top-0 left-8 right-8 h-[2px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 bg-[var(--swipe-accent)] rounded-full" />
                      </a>
                    </Reveal>
                  ))}
                </div>

                <Reveal delay={300}>
                  <p className="mt-10 text-[12.5px] text-slate-500 leading-relaxed max-w-[760px] mx-auto text-center">
                    These documents are provided for informational purposes only and do not
                    constitute legal advice. Requirements vary by state, card brand, and merchant
                    program &mdash; consult your Swipetronics partner manager for specifics.
                  </p>
                </Reveal>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ───────────── Why partner with Swipetronics (preserved) ───────────── */}
      <section className="bg-[var(--swipe-muted)] section border-y border-line">
        <div className="container-swipe grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline mb-4">Why partner with Swipetronics</div>
            <h2 className="font-display-tight text-3xl sm:text-4xl font-bold text-[var(--swipe-navy)] leading-[1.15]">
              Real partner support &mdash; not just a sign-up form.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              We treat our partners like co-founders of the business. Direct lines to underwriting,
              fast deployment, modern hardware, transparent payouts, and a back-office that
              respects your time.
            </p>
            <ul className="mt-7 space-y-3">
              {[
                "Transparent residual reporting",
                "Same-week merchant onboarding",
                "Direct underwriting communication",
                "Co-branded marketing collateral",
                "Dedicated partner account manager",
                "Modern POS catalog & free POS program",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-[15px] text-slate-700">
                  <Check className="w-4 h-4 text-[var(--swipe-accent)] mt-1 shrink-0" strokeWidth={3} />
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[var(--swipe-navy)] text-white rounded-md p-8 border border-white/10">
            <Headphones className="w-7 h-7 text-[var(--swipe-accent)]" />
            <h3 className="mt-4 text-2xl font-extrabold" style={{ color: "#FFFFFF" }}>Ready to talk?</h3>
            <p className="mt-3 leading-relaxed" style={{ color: "rgba(255,255,255,0.78)" }}>
              Tell us a bit about your business and the kind of partnership you&apos;re looking for.
              We&apos;ll respond within one business day with next steps.
            </p>
            <Link to="/contact" className="btn-primary-swipe mt-7 inline-flex" data-testid="partner-cta">
              Become a partner <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Partner with Swipetronics"
        title="Let's build something together."
        description="Our partner program is built around relationships, not transactions. Reach out and we'll find the right fit."
      />
    </div>
  );
}
